import { useQuery } from '@tanstack/react-query';
import { ArrowLeftRight, Boxes, CalendarClock, ChevronRight, Laptop, Plug, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router';
import { DueBadge, IntegrationStatusBadge } from '../components/badges';
import { Card, CardHeader, EmptyState, ErrorState, PageHeader, PageLoader, StatCard } from '../components/ui';
import { api } from '../lib/api';
import { PROVIDER_TITLE } from '../lib/integrations';
import { useAuth } from '../lib/auth';
import { ASSET_STATUS_LABEL, formatNumber, formatRelative, PERSON_TYPE_LABEL } from '../lib/format';
import type { AssetStatus, Dashboard, PersonType } from '../lib/types';

const STATUS_ORDER: AssetStatus[] = ['AVAILABLE', 'CHECKED_OUT', 'IN_REPAIR', 'LOST', 'STOLEN', 'RETIRED', 'DISPOSED'];
const STATUS_COLOR: Record<AssetStatus, string> = {
  AVAILABLE: 'bg-emerald-500',
  CHECKED_OUT: 'bg-sky-500',
  IN_REPAIR: 'bg-amber-400',
  LOST: 'bg-red-500',
  STOLEN: 'bg-red-700',
  RETIRED: 'bg-slate-300',
  DISPOSED: 'bg-slate-400',
};
const PERSON_ORDER: PersonType[] = ['STUDENT', 'TEACHER', 'STAFF', 'CANDIDATE', 'ALUMNI', 'PAST_STUDENT', 'OTHER'];

function greeting() {
  const hour = new Date().getHours();
  return hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
}

export function DashboardPage() {
  const { me } = useAuth();
  const query = useQuery({ queryKey: ['dashboard'], queryFn: () => api.get<Dashboard>('/dashboard'), refetchInterval: 60_000 });

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const data = query.data;
  const firstName = me?.user.name.split(' ')[0];

  return (
    <>
      <PageHeader
        title={`${greeting()}${firstName ? `, ${firstName}` : ''}`}
        description={`Here’s what’s happening with technology at ${me?.tenant?.name}.`}
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total assets"
          value={formatNumber(data.assets.total)}
          sub={`${formatNumber(data.assets.byStatus.AVAILABLE)} available`}
          icon={<Boxes />}
          to="/assets"
        />
        <StatCard
          label="Checked out"
          value={formatNumber(data.checkouts.active)}
          sub={`${formatNumber(data.checkouts.dueThisWeek)} due in the next 7 days`}
          icon={<ArrowLeftRight />}
          to="/checkouts"
          tone="violet"
        />
        <StatCard
          label="Overdue"
          value={formatNumber(data.checkouts.overdue)}
          sub="Past their due date"
          icon={<CalendarClock />}
          to="/checkouts?state=overdue"
          tone={data.checkouts.overdue > 0 ? 'red' : 'green'}
        />
        <StatCard
          label="ChromeOS devices"
          value={formatNumber(data.google.total)}
          sub={`${formatNumber(data.google.logins7d)} sign-ins this week`}
          icon={<Laptop />}
          to="/devices"
          tone="amber"
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Inventory by status" description={`${formatNumber(data.assets.total)} assets`} />
          <div className="p-5">
            <div className="flex h-3 overflow-hidden rounded-full bg-slate-100">
              {STATUS_ORDER.map((status) => {
                const count = data.assets.byStatus[status] ?? 0;
                if (!count || !data.assets.total) return null;
                return (
                  <div
                    key={status}
                    className={STATUS_COLOR[status]}
                    style={{ width: `${(count / data.assets.total) * 100}%` }}
                    title={`${ASSET_STATUS_LABEL[status]}: ${count}`}
                  />
                );
              })}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
              {STATUS_ORDER.map((status) => (
                <Link key={status} to={`/assets?status=${status}`} className="flex items-center gap-2 rounded-md py-1 text-sm hover:text-brand-700">
                  <span className={`size-2.5 rounded-full ${STATUS_COLOR[status]}`} />
                  <span className="text-slate-600">{ASSET_STATUS_LABEL[status]}</span>
                  <span className="ml-auto font-semibold text-slate-900">{formatNumber(data.assets.byStatus[status])}</span>
                </Link>
              ))}
            </div>

            <h3 className="mt-8 mb-3 text-xs font-semibold tracking-wide text-slate-500 uppercase">By category</h3>
            <div className="space-y-2.5">
              {data.assets.byCategory.slice(0, 8).map((category) => (
                <Link
                  key={category.categoryId ?? 'none'}
                  to={`/assets?categoryId=${category.categoryId ?? 'none'}`}
                  className="group grid grid-cols-[10rem_1fr_3rem] items-center gap-3 text-sm"
                >
                  <span className="truncate text-slate-600 group-hover:text-brand-700">{category.name}</span>
                  <span className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <span
                      className="block h-full rounded-full bg-brand-500"
                      style={{ width: `${(category.count / Math.max(1, data.assets.byCategory[0]?.count ?? 1)) * 100}%` }}
                    />
                  </span>
                  <span className="text-right font-medium text-slate-900">{formatNumber(category.count)}</span>
                </Link>
              ))}
            </div>
          </div>
        </Card>

        <div className="space-y-6">
          {[
            {
              title: 'Chromebook fleet health',
              description: 'From Google Admin',
              show: data.google.total > 0 || data.managed.total === 0,
              rows: [
                { label: 'Past auto-update expiration', value: data.google.aueExpired, to: '/devices?aue=expired', warn: data.google.aueExpired > 0 },
                { label: 'Expiring within 12 months', value: data.google.aueWithinYear, to: '/devices?aue=within_year', warn: false },
                { label: 'Not synced in 30+ days', value: data.google.staleOver30Days, to: '/devices?inactiveDays=30', warn: data.google.staleOver30Days > 0 },
                { label: 'Not linked to an asset', value: data.google.unlinked, to: '/devices?linked=false', warn: false },
              ],
            },
            {
              title: 'Managed device health',
              description: 'From your MDM and Apple School Manager',
              show: data.managed.total > 0,
              rows: [
                { label: 'No check-in for 30+ days', value: data.managed.notCheckedIn30Days, to: '/managed-devices?notCheckedInDays=30', warn: data.managed.notCheckedIn30Days > 0 },
                { label: 'Purchased but not enrolled', value: data.managed.notEnrolled, to: '/managed-devices?asm=any&mdm=none', warn: data.managed.notEnrolled > 0 },
                { label: 'Coverage ends within 90 days', value: data.managed.appleCareEndingWithin90Days, to: '/managed-devices?appleCare=within_90', warn: false },
                { label: 'Not linked to an asset', value: data.managed.unlinked, to: '/managed-devices?linked=false', warn: false },
              ],
            },
          ]
            .filter((card) => card.show)
            .map((card) => (
              <Card key={card.title}>
                <CardHeader title={card.title} description={card.description} />
                <ul className="divide-y divide-slate-100">
                  {card.rows.map((row) => (
                    <li key={row.label}>
                      <Link to={row.to} className="flex items-center gap-3 px-5 py-3 text-sm hover:bg-slate-50">
                        {row.warn ? <TriangleAlert className="size-4 text-amber-500" /> : <span className="size-4" />}
                        <span className="flex-1 text-slate-600">{row.label}</span>
                        <span className="font-semibold text-slate-900">{formatNumber(row.value)}</span>
                        <ChevronRight className="size-4 text-slate-300" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title="Overdue checkouts"
            actions={
              <Link to="/checkouts?state=overdue" className="text-sm font-medium text-brand-700 hover:text-brand-800">
                View all
              </Link>
            }
          />
          {data.checkouts.overdueList.length === 0 ? (
            <EmptyState title="Nothing overdue" description="Every checked-out asset is within its due date." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.checkouts.overdueList.map((checkout) => (
                <li key={checkout.id} className="flex items-center gap-4 px-5 py-3 text-sm">
                  <Link to={`/assets/${checkout.asset.id}`} className="w-24 shrink-0 font-mono text-xs font-semibold text-brand-800 hover:underline">
                    {checkout.asset.assetTag}
                  </Link>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-slate-900">{checkout.assigneeLabel}</p>
                    <p className="truncate text-xs text-slate-500">{checkout.asset.name}</p>
                  </div>
                  <DueBadge dueAt={checkout.dueAt} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader
              title="Work orders"
              actions={
                <Link to="/work-orders" className="text-sm font-medium text-brand-700 hover:text-brand-800">
                  View all
                </Link>
              }
            />
            <div className="grid grid-cols-2 gap-px bg-slate-100">
              {[
                { label: 'Open', value: data.workOrders.open, to: '/work-orders' },
                { label: 'Unassigned', value: data.workOrders.unassigned, to: '/work-orders?view=unassigned' },
                { label: 'High or urgent', value: data.workOrders.highPriority, to: '/work-orders?priority=HIGH,URGENT' },
                { label: 'Assigned to me', value: data.workOrders.assignedToMe, to: '/work-orders?view=mine' },
              ].map((tile) => (
                <Link key={tile.label} to={tile.to} className="bg-white px-5 py-3 hover:bg-slate-50">
                  <p className="text-xs text-slate-500">{tile.label}</p>
                  <p className="text-lg font-semibold text-slate-900">{formatNumber(tile.value)}</p>
                </Link>
              ))}
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Integrations"
              actions={
                <Link to="/integrations" className="text-sm font-medium text-brand-700 hover:text-brand-800">
                  Manage
                </Link>
              }
            />
            {data.integrations.length === 0 && <p className="px-5 py-4 text-sm text-slate-500">No integrations are connected yet.</p>}
            <ul className="divide-y divide-slate-100">
              {data.integrations.map((integration) => (
                <li key={integration.provider} className="flex items-center gap-3 px-5 py-3 text-sm">
                  <Plug className="size-4 text-slate-400" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900">
                      {PROVIDER_TITLE[integration.provider]}
                      {integration.mode === 'DEMO' && <span className="ml-1.5 text-xs font-normal text-slate-400">(demo data)</span>}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      {integration.lastError
                        ? integration.lastError
                        : integration.lastSuccessAt
                          ? `Synced ${formatRelative(integration.lastSuccessAt)}`
                          : 'Never synced'}
                    </p>
                  </div>
                  <IntegrationStatusBadge status={integration.status} enabled={integration.enabled} />
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <CardHeader title="People" description="Active records" />
            <ul className="grid grid-cols-2 gap-px bg-slate-100">
              {PERSON_ORDER.filter((type) => data.people.byType[type]).map((type) => (
                <li key={type} className="bg-white">
                  <Link to={`/people?type=${type}`} className="block px-5 py-3 hover:bg-slate-50">
                    <p className="text-xs text-slate-500">{PERSON_TYPE_LABEL[type]}</p>
                    <p className="text-lg font-semibold text-slate-900">{formatNumber(data.people.byType[type])}</p>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>

      <Card className="mt-6">
        <CardHeader title="Recent activity" />
        {data.recentActivity.length === 0 ? (
          <EmptyState title="No activity yet" />
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.recentActivity.map((entry) => (
              <li key={entry.id} className="flex items-start gap-4 px-5 py-3 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="text-slate-800">
                    {entry.entityType === 'Asset' && entry.entityId ? (
                      <Link to={`/assets/${entry.entityId}`} className="hover:text-brand-700">
                        {entry.summary}
                      </Link>
                    ) : (
                      entry.summary
                    )}
                  </p>
                  <p className="text-xs text-slate-500">{entry.actorName ?? 'System'}</p>
                </div>
                <span className="shrink-0 text-xs text-slate-400">{formatRelative(entry.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
